import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import emailjs from '@emailjs/browser';

const FOCUSABLE_SELECTORS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const ContactModal = ({ isOpen, onClose, triggerRef }) => {
    const [formData, setFormData] = useState({ name: '', email: '', title: '', message: '' });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const modalRef = useRef(null);
    const headingId = 'contact-modal-heading';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const templateParams = {
        name: formData.name,
        email: formData.email,
        title: formData.title,
        message: formData.message,
    };

    try {
        // Send both emails in parallel:
        // 1. Auto-reply to the visitor
        // 2. Notification to you with the message content
        await Promise.all([
            emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                templateParams,
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            ),
            emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID_NOTIFY,
                templateParams,
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            ),
        ]);
        setIsSubmitted(true);
    } catch (err) {
        console.error('EmailJS error:', err);
        setError('Something went wrong. Please try again or email me directly.');
    } finally {
        setIsSubmitting(false);
    }
};

    const handleClose = useCallback(() => {
        setIsSubmitted(false);
        setError(null);
        setFormData({ name: '', email: '', title: '', message: '' });
        onClose();
    }, [onClose]);

    // Focus management: move focus into modal on open, return it on close
    useEffect(() => {
        if (!isOpen) return;

        const modal = modalRef.current;
        if (!modal) return;

        // Focus the first focusable element when modal opens
        const focusables = modal.querySelectorAll(FOCUSABLE_SELECTORS);
        if (focusables.length) focusables[0].focus();

        const trapFocus = (e) => {
            if (e.key === 'Escape') {
                handleClose();
                return;
            }
            if (e.key !== 'Tab') return;

            const all = modal.querySelectorAll(FOCUSABLE_SELECTORS);
            const first = all[0];
            const last = all[all.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        const trigger = triggerRef?.current;
        modal.addEventListener('keydown', trapFocus);
        return () => {
            modal.removeEventListener('keydown', trapFocus);
            // Return focus to the element that opened the modal
            trigger?.focus();
        };
    }, [isOpen, handleClose, triggerRef]);

    if (!isOpen) return null;

    return (
        <div
            className="contact-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div
                ref={modalRef}
                className="contact-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={headingId}
            >
                <button className="close-modal" onClick={handleClose} aria-label="Close contact form">
                    &times;
                </button>

                {isSubmitted ? (
                    <div className="modal-success">
                        <h2 id={headingId} className="section-title">Message Sent</h2>
                        <p className="modal-success-text">
                            Thanks for reaching out, {formData.name || 'friend'}! I'll get back to you soon.
                        </p>
                        <button className="submit-btn" onClick={handleClose}>
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <h2 id={headingId} className="section-title">Contact Me</h2>
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    autoComplete="name"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    autoComplete="email"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="title">Subject</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                ></textarea>
                            </div>
                            {error && (
                                <p className="form-error" role="alert" style={{ color: '#d32f2f', marginBottom: '1rem' }}>
                                    {error}
                                </p>
                            )}
                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

const Contact = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const triggerRef = useRef(null);

    return (
        <>
            <section id="contact" className="contact section">
                <div className="container contact-actions">
                    <button
                        ref={triggerRef}
                        className="contact-btn"
                        onClick={() => setIsModalOpen(true)}
                    >
                        CONTACT ME
                    </button>
                    <a href="/CaryZhu-Resume-.pdf" className="resume-btn" download>
                        <FontAwesomeIcon icon={faDownload} aria-hidden="true" /> DOWNLOAD RESUME
                    </a>
                </div>
            </section>
            <ContactModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                triggerRef={triggerRef}
            />
        </>
    );
};

export default Contact;